import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Calendar, CreditCard, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import ProgressBar from '@/components/ProgressBar';
import { Button } from '@/components/ui/button';
import { getRequestedItems } from '@/lib/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const STATUS_LABELS = {
  pending: 'In attesa',
  requested: 'Richiesto',
  approved: 'Approvato',
  processing: 'In elaborazione',
  shipped: 'Spedito',
  completed: 'Completato'
};

const TYPE_LABELS = {
  exchange: 'Cambio',
  voucher: 'Voucher',
  rma: 'Reso',
  refund: 'Rimborso'
};

const OrderDetails = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [requestedItems, setRequestedItems] = useState([]);
  const [requestedVariantIds, setRequestedVariantIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const storedOrder = sessionStorage.getItem('currentOrder');
      if (storedOrder) {
        const orderData = JSON.parse(storedOrder);
        
        // Expand products with quantity > 1 into individual items
        const expandedProducts = [];
        orderData.products.forEach(product => {
          for (let i = 0; i < product.quantity; i++) {
            expandedProducts.push({
              ...product,
              // Create unique ID for each unit
              unique_id: `${product.variant_id}_${i}`,
              quantity: 1,
              original_quantity: product.quantity
            });
          }
        });
        
        setOrder({
          ...orderData,
          products: expandedProducts
        });
        
        // Load items already requested for this order
        try {
          const data = await getRequestedItems(orderData.order_number);
          setRequestedItems(data.requested_items || []);
          setRequestedVariantIds(data.requested_variant_ids || []);
        } catch (error) {
          console.error('Error loading requested items:', error);
        }
      } else {
        navigate('/');
      }
      setIsLoading(false);
    };
    
    loadData();
  }, [navigate]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: it });
    } catch {
      return dateString;
    }
  };

  // Check if a product is already requested
  const isProductRequested = (product) => {
    return requestedVariantIds.includes(product.variant_id);
  };

  // Filter available and requested products
  const availableProducts = order?.products?.filter(p => !isProductRequested(p)) || [];
  const alreadyRequestedProducts = order?.products?.filter(p => isProductRequested(p)) || [];

  const getRequestInfoForVariant = (variantId) => {
    return requestedItems.find(item => item.variant_id === variantId);
  };

  const toggleItem = (product) => {
    if (isProductRequested(product)) {
      toast.error('Questo articolo ha già una richiesta in corso');
      return;
    }
    
    setSelectedItems(prev => {
      const isSelected = prev.find(p => p.unique_id === product.unique_id);
      if (isSelected) {
        return prev.filter(p => p.unique_id !== product.unique_id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleContinue = () => {
    if (selectedItems.length === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }
    
    sessionStorage.setItem('selectedItems', JSON.stringify(selectedItems));
    navigate(`/order/${orderNumber}/type`);
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Caricamento...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  // Check if all items are already requested
  const allItemsRequested = availableProducts.length === 0 && order.products.length > 0;

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}`} />
      <ProgressBar currentStep={1} totalSteps={5} />
      
      <main className="flex-1 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Passaggio 1 di 5
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-normal tracking-tight mb-2">
              Ordine {order.order_number}
            </h1>
            <p className="text-muted-foreground font-light mb-8">
              {order.customer_name} • {order.email}
            </p>
          </motion.div>

          {/* Order Info */}
          <motion.div 
            className="grid grid-cols-2 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="luxury-card p-6">
              <Calendar className="w-5 h-5 text-muted-foreground mb-3" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Data Ordine</p>
              <p className="font-medium">{formatDate(order.created_at)}</p>
            </div>
            <div className="luxury-card p-6">
              <CreditCard className="w-5 h-5 text-muted-foreground mb-3" />
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Totale</p>
              {order.is_exchange_order ? (
                <p className="font-medium text-sm text-muted-foreground">Ordine di cambio</p>
              ) : (
                <p className="font-medium">€{parseFloat(order.total_price).toFixed(2)}</p>
              )}
            </div>
          </motion.div>

          {/* Alert: All items already requested */}
          {allItemsRequested && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border-2 border-amber-300 p-6 mb-8"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">
                    Tutti gli articoli hanno già una richiesta
                  </h3>
                  <p className="text-amber-800 text-sm mb-4">
                    Hai già effettuato una richiesta per tutti gli articoli di questo ordine. 
                    Non è possibile fare più di una richiesta sullo stesso prodotto.
                  </p>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                  >
                    Torna alla home
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Already Requested Products */}
          {alreadyRequestedProducts.length > 0 && !allItemsRequested && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mb-8"
            >
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Articoli con richiesta già in corso
              </p>
              <div className="space-y-3 opacity-60">
                {alreadyRequestedProducts.map((product) => {
                  const requestInfo = getRequestInfoForVariant(product.variant_id);
                  return (
                    <div 
                      key={product.unique_id}
                      className="border border-border bg-zinc-50 p-4 flex items-center gap-4"
                    >
                      <div className="w-6 h-6 border border-zinc-300 bg-zinc-200 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div className="w-16 h-20 bg-white overflow-hidden flex-shrink-0 border">
                        {product.image_url && (
                          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover grayscale" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.title}</p>
                        <p className="text-xs text-muted-foreground">{product.variant_title}</p>
                        {requestInfo && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs bg-zinc-200 text-zinc-700 px-2 py-0.5 rounded">
                              {TYPE_LABELS[requestInfo.request_type] || requestInfo.request_type}
                            </span>
                            <span className="text-xs text-zinc-500">
                              {STATUS_LABELS[requestInfo.status] || requestInfo.status}
                            </span>
                          </div>
                        )}
                      </div>
                      {!order?.is_exchange_order && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">€{parseFloat(product.price).toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Divider if there are both types */}
          {alreadyRequestedProducts.length > 0 && availableProducts.length > 0 && (
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Seleziona i prodotti da rendere o cambiare
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* Products Selection - Only available products */}
          {availableProducts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {alreadyRequestedProducts.length === 0 && (
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-6">
                  Seleziona i prodotti da rendere o cambiare
                </p>
              )}
              
              <div className="space-y-4 mb-8">
                {availableProducts.map((product, index) => {
                  const isSelected = selectedItems.some(p => p.unique_id === product.unique_id);
                  
                  return (
                    <motion.div 
                      key={product.unique_id}
                      className={cn(
                        "product-card cursor-pointer transition-all",
                        isSelected && "selected border-primary bg-secondary/30"
                      )}
                      onClick={() => toggleItem(product)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      data-testid={`order-product-${product.id}`}
                    >
                      {/* Checkbox */}
                      <div className={cn(
                        "w-6 h-6 border flex items-center justify-center flex-shrink-0 transition-all",
                        isSelected ? "bg-primary border-primary" : "border-border"
                      )}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      
                      <div className="w-24 h-32 flex-shrink-0 bg-secondary overflow-hidden">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                            No Image
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-normal tracking-tight">
                          {product.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {product.variant_title}
                        </p>
                        <div className="flex items-center gap-6 mt-3">
                          <span className="text-xs text-muted-foreground">
                            Quantità: {product.quantity}
                          </span>
                          {!order?.is_exchange_order && (
                            <span className="text-sm font-medium">
                              €{parseFloat(product.price).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Action Button */}
          {!allItemsRequested && (
            <motion.div
              className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="max-w-3xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedItems.length > 0 && (
                    <>
                      <div className="w-8 h-8 bg-primary text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                      <span className="text-sm">
                        {selectedItems.length} prodott{selectedItems.length === 1 ? 'o' : 'i'} selezionat{selectedItems.length === 1 ? 'o' : 'i'}
                      </span>
                    </>
                  )}
                </div>
                
                <Button 
                  onClick={handleContinue}
                  disabled={selectedItems.length === 0}
                  className="luxury-btn-primary inline-flex items-center gap-3"
                  data-testid="continue-btn"
                >
                  Continua
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Spacer for fixed footer */}
          {!allItemsRequested && <div className="h-24" />}
        </div>
      </main>
    </div>
  );
};

export default OrderDetails;
